import { observable, action } from "mobx";
import { ServiceInterface } from "../service.interface";
import { DefaultLogger } from "../../logger/logger";
import { createContext } from "react";
import {
  ARSupported,
  DeviceAdapterInterface,
} from "./device-adapter.interface";

export const DeviceServiceContext = createContext<DeviceService>(null as any);

export class DeviceService implements ServiceInterface {
  public context = DeviceServiceContext;

  @observable
  public hasGyroscope = true;

  constructor(private deviceAdapter: DeviceAdapterInterface) {}

  @action
  private setHasGyroscope = (gyroscope: boolean) =>
    (this.hasGyroscope = gyroscope);

  @observable
  public isARSupported: ARSupported = "UNSUPPORTED";

  @action
  private setIsARSupported = (supported: ARSupported) =>
    (this.isARSupported = supported);

  public async serviceDidBecomeForeground() {
    this.refreshHasGyroscope();
    this.refreshARSupported();
  }

  public getDevUrl = () => {
    return this.deviceAdapter.getDevUrl?.();
  };

  public getApiPath = () => {
    return this.deviceAdapter.getApiPath?.();
  };

  public getInstallReferrer = () => {
    return this.deviceAdapter.getInstallReferrer?.();
  };

  public refreshHasGyroscope = () => {
    this.deviceAdapter.hasGyroscope().then(this.setHasGyroscope);
  };

  public requestARServiceInstall = async () => {
    await this.deviceAdapter.requestARInstall();
    this.refreshARSupported();
  };

  private isCheckingSupport = false;

  public refreshARSupported = () => {
    if (this.isCheckingSupport) return;

    this.isCheckingSupport = true;

    this.deviceAdapter
      .checkIsARSupported()
      .then((supported) => {
        this.setIsARSupported(supported);
        if (this.isARSupported === "PENDING") {
          setTimeout(() => {
            this.refreshARSupported();
          }, 500);
        }
      })
      .catch((e) => DefaultLogger.captureException(e))
      .finally(() => (this.isCheckingSupport = false));
  };

  public getDeviceLanguage = () => {
    return this.deviceAdapter.getDeviceLanguage();
  };

  public getPreferredLanguages = () => {
    return this.deviceAdapter.getPreferredLanguages?.();
  };

  public getPreferredLanguagesShort = () => {
    return (
      this.deviceAdapter.getPreferredLanguages?.().map((lang) => lang.short) ||
      []
    );
  };

  /**
   * Get battery level as a percentage between 0 and 100%
   */
  public getBatteryLevel = async (): Promise<number> => {
    return Math.round((await this.deviceAdapter.getBatteryLevel()) * 100);
  };

  public isEmulator = () => this.deviceAdapter.isEmulator?.();
}
